import { queryOptions, useQuery } from '@tanstack/react-query';
import { SoilClassificationDTO, PredictionsService, SoilClassificationOutputDTO } from '../generated';
import { AttributeChipData } from '../../components/Prediction/types';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { createElement } from 'react';
import { getConfidenceLevel } from '../../components/Prediction/utils';
import GrainIcon from '@mui/icons-material/Grain';

export interface SoilClassificationOptionProps {
  jobsiteId: string | undefined;
  imageId: string | undefined;
  enabled?: boolean;
}

export function soilClassificationOptions({ jobsiteId, imageId, enabled = true }: SoilClassificationOptionProps) {
  const transformFn = (resp: SoilClassificationDTO) => transformSoilClassification(resp?.output || {});

  return queryOptions<AttributeChipData[], ApiError>({
    queryKey: ['soilClassification', jobsiteId, imageId],
    queryFn: async () => {
      if (!jobsiteId || !imageId) {
        throw new Error('jobsiteId and imageId are required');
      }

      const response = await PredictionsService.predictionControllerGetSoilClassificationByImageId({
        path: { jobsiteId, imageId },
      });

      if (response.data !== undefined) {
        return transformFn(response.data);
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!imageId && enabled,
  });
}

export function useSoilClassification(props: SoilClassificationOptionProps) {
  return useQuery(soilClassificationOptions(props));
}

export function transformSoilClassification(output: SoilClassificationOutputDTO): AttributeChipData[] {
  const chips: AttributeChipData[] = [];
  Object.entries(output).forEach(([category, values]) => {
    if (values && typeof values === 'object') {
      Object.entries(values as Record<string, number>).forEach(([v, p], index) => {
        // Only display predictions above 0.5 threshold
        if (p >= 0.5) {
          const probability = getConfidenceLevel(p);

          chips.push({
            id: `soil-${category}-${v}-${index}`,
            name: `attributes.soilClassification.categories.${category}.label`,
            value: `attributes.soilClassification.categories.${category}.values.${v}`,
            status: 'OK',
            icon: createElement(GrainIcon, { sx: { flexShrink: 0, marginLeft: 0.5 } }),
            probability: probability,
          });
        }
      });
    }
  });
  const probabilityOrder = { high: 3, medium: 2, low: 1 };
  return chips.sort((a, b) => {
    const aPriority = a.probability ? probabilityOrder[a.probability] : 0;
    const bPriority = b.probability ? probabilityOrder[b.probability] : 0;
    return bPriority - aPriority;
  });
}
